import { Link } from '@tanstack/react-router'
import { TriangleAlert } from 'lucide-react'
import { STORE_TAB_READ_PERMISSIONS } from '~/components/routes/stores/tabs'
import { cn } from '~/lib/utils'
import { withoutDetailTab } from '~/routes/_authenticated/logistics/-search-schema'
import { usePermissionGuard } from '~/utils/rbac'
import { diagnosticSubjectLabel } from './run-status'
import {
  placeSubjectOf,
  STATUS_LABEL_TO_CHECK,
  runWarningsOfTab,
  runWarningStatusMessageOf,
  worstRunWarningLevelOf,
  type RunWarning,
  type RunWarningLevel,
} from './run-warnings'
import type { ScenarioPlace } from './scenario-view'
import { REMOVED_PLACE_LABEL } from './simulator-list'
import { LOGISTICS_HUB_READ } from './tabs'

/** Rouge : la bascule en vigueur est refusée ; orange : à vérifier (#975). */
const LEVEL_STYLES: Record<RunWarningLevel, { box: string; icon: string; dot: string }> = {
  red: {
    box: 'bg-destructive-background text-destructive',
    icon: 'text-destructive',
    dot: 'bg-destructive',
  },
  orange: {
    box: 'bg-secondary-orange-100 text-secondary-orange-900',
    icon: 'text-secondary-orange-600',
    dot: 'bg-secondary-orange-600',
  },
}

const LEVEL_LABELS: Record<RunWarningLevel, string> = {
  red: 'bloquant',
  orange: 'à vérifier',
}

/** Fond et texte d'un badge de statut au niveau donné (liste des tournées). */
export const runWarningBadgeClassOf = (level: RunWarningLevel): string => LEVEL_STYLES[level].box

/**
 * Pastille du plus grave : onglet du détail, repère de la carte. `label` : son texte pour un lecteur
 * d'écran, quand le sens du rouge n'est pas « bloquant » (tournée validée, #982).
 */
export function RunWarningDot({
  level,
  label = LEVEL_LABELS[level],
  className,
  dotClassName,
}: {
  level: RunWarningLevel
  label?: string
  className?: string
  dotClassName?: string
}) {
  return (
    <span className={cn('inline-flex shrink-0', className)}>
      <span
        aria-hidden="true"
        className={cn('size-2 rounded-full', LEVEL_STYLES[level].dot, dotClassName)}
      />
      <span className="sr-only">({label})</span>
    </span>
  )
}

export function RunWarningIcon({
  level,
  className,
}: {
  level: RunWarningLevel
  className?: string
}) {
  return (
    <TriangleAlert
      aria-hidden="true"
      className={cn('size-4 shrink-0', LEVEL_STYLES[level].icon, className)}
    />
  )
}

/**
 * Bloc de statut d'une tournée faisable avec avertissements : « À vérifier », le nombre de points
 * et, en lignes, ceux qu'aucun onglet ne montre.
 */
export function RunWarningsStatus({
  warnings,
  placeLabel,
}: {
  warnings: readonly RunWarning[]
  placeLabel: (place: ScenarioPlace) => string
}) {
  const level = worstRunWarningLevelOf(warnings)
  if (level === null) return null
  return (
    <div
      role="status"
      className={cn('flex flex-col gap-1.5 rounded-xl p-3 text-sm', LEVEL_STYLES[level].box)}
    >
      <p className="flex items-center gap-1.5 font-semibold">
        <RunWarningIcon level={level} />
        {STATUS_LABEL_TO_CHECK}
      </p>
      <p>{runWarningStatusMessageOf(warnings)}</p>
      <RunWarningList warnings={runWarningsOfTab(warnings, null)} placeLabel={placeLabel} />
    </div>
  )
}

/**
 * Avertissements à l'endroit qu'ils concernent : message, lieu (sauf `withSubject` à `false`, sur
 * la ligne du lieu) et lien vers sa fiche, si l'utilisateur peut la lire.
 */
export function RunWarningList({
  warnings,
  placeLabel,
  withSubject = true,
  className,
}: {
  warnings: readonly RunWarning[]
  placeLabel: (place: ScenarioPlace) => string
  withSubject?: boolean
  className?: string
}) {
  if (warnings.length === 0) return null
  return (
    <ul className={cn('flex flex-col gap-1.5', className)} aria-label="Points à vérifier">
      {warnings.map((warning, index) => (
        // Clé = index : l'API dédoublonne la liste, son ordre ne change pas.
        <li
          key={index}
          className={cn(
            'flex items-start gap-2 rounded-xl px-3 py-2 text-xs leading-4',
            LEVEL_STYLES[warning.level].box,
          )}
        >
          <RunWarningIcon level={warning.level} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="break-words">
              {withSubject && warning.subject !== undefined && (
                <span className="font-semibold">
                  {diagnosticSubjectLabel(warning.subject, placeLabel)} :{' '}
                </span>
              )}
              {warning.message}
            </p>
            <RunWarningPlaceLink warning={warning} placeLabel={placeLabel} />
          </div>
        </li>
      ))}
    </ul>
  )
}

const LINK_CLASS = 'w-fit font-semibold underline underline-offset-2'

/**
 * Fiche du lieu à corriger : le hub déclare ses créneaux d'ouverture ; la boutique, faute d'écran
 * pour son profil logistique (créneaux de réception), ouvre sa fiche. Sans lien pour les autres
 * sujets, pour un lieu supprimé, ni sans le droit de lire la fiche.
 */
function RunWarningPlaceLink({
  warning,
  placeLabel,
}: {
  warning: RunWarning
  placeLabel: (place: ScenarioPlace) => string
}) {
  const canReadStore = usePermissionGuard({ any: STORE_TAB_READ_PERMISSIONS }).authorized
  const canReadHub = usePermissionGuard(LOGISTICS_HUB_READ).authorized
  const subject = placeSubjectOf(warning)
  // Lieu supprimé depuis le calcul : plus de fiche à ouvrir.
  if (subject === null || placeLabel(subject) === REMOVED_PLACE_LABEL) return null
  if (subject.type === 'store' && canReadStore) {
    return (
      <Link to="/stores/$storeId" params={{ storeId: subject.id }} className={LINK_CLASS}>
        Ouvrir la fiche de la boutique
      </Link>
    )
  }
  if (subject.type === 'hub' && canReadHub) {
    return (
      <Link
        from="/logistics/simulations/$simulationId"
        to="/logistics/hubs/$hubId"
        params={{ hubId: subject.id }}
        search={withoutDetailTab}
        className={LINK_CLASS}
      >
        Ouvrir la fiche du hub
      </Link>
    )
  }
  return null
}
